import React, { useEffect, useState } from 'react';
import { Bubble, Button, Icon, Tabs } from 'tea-component';
import moment from 'moment';
import { getLocale, useIntl } from 'umi';
import { getContentDetail } from '@op/utils/languageValue';
import SvgIcon from '@home/components/SvgIcon';

interface ReportInsightItem {
  date: string; // 假设 date 是字符串格式，如 "2024-02-18"
  title: string;
  title_en?: string; // 英文标题（如果有）
  id: string;
}

interface SegmentTabProps {
  reportInsightConfig?: ReportInsightItem[];
  download: (fileName: string) => void;
  handleInit?: (data: { date: string; title: string; id: string; }) => void;
  handleReportChange?: (data: { date: string; title?: string; }) => Promise<void>;
  setReportId:(id: string ) => (void);
  className?: string;
  style?: React.CSSProperties;
  fileType?: string;
  downloadLoading?: boolean;
}

export default function SegmentTab(props: SegmentTabProps) {
  const { reportInsightConfig = [], download, fileType, handleInit, handleReportChange, className, style, downloadLoading, setReportId } = props;
  const [tabList, setTabList] = useState<Array<any>>([]);
  const [activeId, setActiveId] = useState('');
  const [tabs, setTabs] = useState<Array<{ id: string; label: string; }>>([]);
  const { formatMessage } = useIntl();

  useEffect(() => {
    // 检查reportInsightConfig是否为有效的数组且长度大于0
    if (
      reportInsightConfig &&
      Array.isArray(reportInsightConfig) &&
      reportInsightConfig?.length > 0
    ) {
      // 根据日期对reportInsightConfig进行降序排序
      const curTabs = reportInsightConfig.sort((a: { date: moment.MomentInput; }, b: { date: moment.MomentInput; }) => {
        if (moment(b.date).isAfter(moment(a.date))) {
          return 1;
        } if (moment(b.date).isBefore(moment(a.date))) {
          return -1;
        } return 0;
      });
      // 更新tab列表状态
      setTabList(curTabs);

      // 设置第一个tab为激活状态
      setActiveId(curTabs[0].id);

      // 调用初始化回调函数，传入第一个tab的日期和标题
      handleInit?.({
        date: curTabs[0].date,
        title: curTabs[0].title,
        id: curTabs[0].id,
      });
    }
  }, [reportInsightConfig]);

  useEffect(() => {
    // 获取当前语言环境,默认为英文
    const key = getLocale()?.split('-')?.[0] || 'en';

    // 根据tabList生成标签页配置
    setTabs(
      tabList.map((item) => ({
        id: item.id,
        date: item.date,
        // 根据语言环境获取对应的标题内容
        label: getContentDetail(
          {
            origin: 'title',
            en: 'title_en',
            zh: 'title',
          },
          key,
          item,
        ),
      })),
    );
  }, [formatMessage, tabList]); 

  const tabBarRender = (children: JSX.Element, tab: any) => (
    <div
      onClick={() =>
        onReportChange({
          id: tab.id,
          title: tab.label,
          date: tab.date,
        })
      }
    >
      {children}
    </div>
  );

  const onReportChange = (child: { date: any; title?: any; id: string; }) => {
    handleReportChange?.(child);
    setReportId(child.id);
    setActiveId(child.id);
  };

  const downloadPdf = () => {
    const item = tabs.find((info: { id: any; }) => info.id === activeId);

    download(item.label || activeId);
  };

  return (
    <div className="dbo-segment-wrapper">
      <Tabs
        tabs={tabs}
        activeId={activeId}
        tabBarRender={tabBarRender}
        tabBarStyle={{}}
        className={`dbo-segment-tab ${className}`}
        style={style}
      ></Tabs>
      <Bubble
        dark
        closeOnScroll
        placement="left"
        content={formatMessage({ id: 'dbh_download_this_report' })}
      >
        {fileType === "img" && 
            <Button
              type="icon"
              className="dbo-downloadbutton ns-btn--icon"
              onClick={downloadPdf}
            >
              {downloadLoading ? <Icon type="loading" /> : <SvgIcon type="download" /> }
            </Button>
        }
      </Bubble>
    </div>
  );
}
